import { useEffect, useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

/**
 * Hook qui retourne la remise (price tier) applicable au user connecté.
 * Cache en mémoire pour éviter de re-fetch à chaque rendu (un appel par session user).
 *
 * Returns: { tier: { id, organization_type, label, discount_percent } | null, loading }
 */
let _cache = { userId: null, data: null };

export function useUserDiscount() {
  const { user } = useAuth();
  const [tier, setTier] = useState(_cache.userId === user?.user_id ? _cache.data : null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'buyer') {
      _cache = { userId: null, data: null };
      setTier(null);
      return;
    }
    if (_cache.userId === user.user_id) {
      setTier(_cache.data);
      return;
    }
    setLoading(true);
    api.get('/discounts/me')
      .then(r => {
        _cache = { userId: user.user_id, data: r.data?.tier || null };
        setTier(_cache.data);
      })
      .catch(() => { /* silent — discount is an enhancement, not critical */ })
      .finally(() => setLoading(false));
  }, [user]);

  return { tier, loading };
}

/** Applique une remise à un prix. Retourne `{ original, discounted, hasDiscount }`. */
export function applyDiscount(price, tier) {
  if (!tier || !tier.discount_percent || !price) {
    return { original: price, discounted: price, hasDiscount: false };
  }
  const discounted = Math.round(price * (1 - tier.discount_percent / 100));
  return { original: price, discounted, hasDiscount: true, percent: tier.discount_percent };
}
